import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import {
  calificarEntrega,
  obtenerAsignacionesPorClase,
  getGrupoMatricula,
  obtenerEntregasPorAsignacion,
  descargarEntrega,
} from "./constants";
import { useData } from "./DataContext";
import NavigationIcon from "../Others/NavigationIcon";
import Cabecera from "../Others/Cabecera";
import BarraSuperior from "../Others/BarraSuperior";
import BarraInferior from "../Others/BarraInferior";
import PiePagina from "../Others/PiePagina";
import "./PaginaInicio.css";
import "./HomeworkModule.css";

const TeacherGradeHomework = () => {
  const { dataClase } = useData();
  const location = useLocation();
  const [tareas, setTareas] = useState([]);
  const [calificaciones, setCalificaciones] = useState({});
  const [comentarios, setComentarios] = useState({});
  const [alumnos, setAlumnos] = useState([]);
  const [entregas, setEntregas] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [status, setStatus] = useState({ type: "", text: "" });

  const obtenerTodasLasEntregas = async (idsAsignaciones) => {
    const nuevoEstado = {};

    for (const id of idsAsignaciones) {
      const response = await axios.get(obtenerEntregasPorAsignacion(id));

      if (response.data && Array.isArray(response.data.data)) {
        response.data.data.forEach((entrega) => {
          const idAlumno = String(entrega.id_alumno);
          if (!nuevoEstado[idAlumno]) {
            nuevoEstado[idAlumno] = {};
          }
          nuevoEstado[idAlumno][String(id)] = entrega;
        });
      }
    }

    setEntregas(nuevoEstado);
  };

  const obtenerTareasAsignadas = async () => {
    const url = obtenerAsignacionesPorClase(dataClase.matricula_clase);
    const response = await axios.get(url);
    const asignaciones = Array.isArray(response.data) ? response.data : [];
    setTareas(asignaciones);
    await obtenerTodasLasEntregas(
      asignaciones.map((asignacion) => asignacion.id_asignacion)
    );
  };

  const obtenerAlumnos = async () => {
    const response = await axios.post(getGrupoMatricula, {
      matricula_clase: dataClase.matricula_clase,
    });

    if (response.data && Array.isArray(response.data.data)) {
      setAlumnos(
        response.data.data.map((alumno) => ({
          matricula: alumno.id_alumno,
          nombre: alumno.nombre_alumno + " " + alumno.apellido_alumno,
        }))
      );
    } else {
      setAlumnos([]);
    }
  };

  useEffect(() => {
    if (!dataClase?.matricula_clase) return;
    let isMounted = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        await obtenerAlumnos();
        await obtenerTareasAsignadas();
        if (isMounted) {
          setStatus({ type: "", text: "" });
        }
      } catch (error) {
        console.error("Error al cargar las entregas:", error);
        if (isMounted) {
          setTareas([]);
          setStatus({
            type: "error",
            text: "No fue posible cargar las tareas y entregas del grupo.",
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      isMounted = false;
    };
  }, [dataClase?.matricula_clase]);

  const handleCalificar = async (idEntrega) => {
    const nota = calificaciones[idEntrega];
    const comentario = comentarios[idEntrega];

    if (!nota) {
      setStatus({
        type: "error",
        text: "Ingresa una calificación antes de guardar.",
      });
      return;
    }

    const confirmacion = window.confirm(
      "¿Deseas guardar la calificación " +
        nota +
        " con el comentario indicado?"
    );
    if (!confirmacion) return;

    try {
      setSavingId(idEntrega);
      setStatus({ type: "", text: "" });
      const response = await axios.put(calificarEntrega, {
        id_entrega: idEntrega,
        calificacion: parseFloat(nota),
        comentarios: comentario || "",
      });

      if (response.data.success) {
        setStatus({
          type: "success",
          text: "La calificación y el comentario se guardaron correctamente.",
        });
      } else {
        setStatus({
          type: "error",
          text: response.data.error || "No fue posible guardar la calificación.",
        });
      }
    } catch (error) {
      console.error("Error al guardar la calificación:", error);
      setStatus({
        type: "error",
        text: "Ocurrió un error al guardar la calificación.",
      });
    } finally {
      setSavingId(null);
    }
  };

  const descargarContenido = async (idEntrega) => {
    try {
      setDownloadingId(idEntrega);
      setStatus({ type: "", text: "" });
      const response = await axios.get(
        descargarEntrega(idEntrega),
        { responseType: "blob" }
      );

      if (response.status === 200) {
        let nombreArchivo = "archivo_descargado.pdf";
        const contentDisposition = response.headers["content-disposition"];
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?([^"]+)"?/);
          if (match && match[1]) {
            nombreArchivo = match[1];
          }
        }

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", nombreArchivo);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } else {
        setStatus({
          type: "error",
          text: "No se encontró una entrega para este alumno.",
        });
      }
    } catch (error) {
      console.error("Error al descargar la entrega:", error);
      setStatus({
        type: "error",
        text: "No fue posible descargar la entrega. Intenta nuevamente.",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const content = (
    <section className="homework-section" aria-labelledby="grade-homework-title">
      <header className="homework-section-header">
        <div>
          <h2 id="grade-homework-title">Revisión y calificación</h2>
          <p>Consulta las entregas del grupo y registra la retroalimentación.</p>
        </div>
        <span className="homework-badge">
          {tareas.length} {tareas.length === 1 ? "tarea" : "tareas"}
        </span>
      </header>

      {status.text && (
        <div
          className={"homework-status is-" + status.type}
          role={status.type === "error" ? "alert" : "status"}
        >
          {status.text}
        </div>
      )}

      {loading ? (
        <div className="homework-loading" aria-live="polite">
          <span className="homework-spinner" />
          <strong>Cargando entregas</strong>
          <p>Estamos consultando las tareas y los alumnos del grupo.</p>
        </div>
      ) : tareas.length === 0 ? (
        <div className="homework-empty">
          <span className="homework-empty-icon">
            <NavigationIcon name="deliverables" size={28} />
          </span>
          <strong>No hay tareas para calificar</strong>
          <p>Cuando se asigne una actividad, sus entregas aparecerán aquí.</p>
        </div>
      ) : (
        <div className="homework-workspace">
          {tareas.map((tarea) => (
            <section
              className="homework-section"
              key={tarea.id_asignacion}
              aria-labelledby={"task-grade-" + tarea.id_asignacion}
            >
              <header className="homework-section-header">
                <div>
                  <span className="homework-kicker">Entregas de la actividad</span>
                  <h3 id={"task-grade-" + tarea.id_asignacion}>{tarea.nombre}</h3>
                </div>
              </header>

              <div className="homework-table-wrap">
                <table className="homework-table">
                  <thead>
                    <tr>
                      <th>Estudiante</th>
                      <th>Entrega</th>
                      <th>Calificación</th>
                      <th>Comentarios</th>
                      <th>Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {alumnos.map((alumno) => {
                      const tareaEntregada =
                        entregas[alumno.matricula]?.[tarea.id_asignacion];
                      const estaGuardando =
                        tareaEntregada && savingId === tareaEntregada.id_entrega;
                      const estaDescargando =
                        tareaEntregada &&
                        downloadingId === tareaEntregada.id_entrega;

                      return (
                        <tr key={alumno.matricula}>
                          <td>{alumno.nombre}</td>
                          <td>
                            {tareaEntregada ? (
                              <button
                                type="button"
                                className="homework-button-secondary"
                                disabled={estaDescargando}
                                onClick={() =>
                                  descargarContenido(tareaEntregada.id_entrega)
                                }
                              >
                                <NavigationIcon name="deliverables" size={17} />
                                {estaDescargando ? "Descargando..." : "Descargar"}
                              </button>
                            ) : (
                              <span className="homework-badge is-warning">
                                No entregado
                              </span>
                            )}
                          </td>
                          <td>
                            {tareaEntregada && (
                              <input
                                className="homework-grade-input"
                                type="number"
                                step="0.1"
                                min="0"
                                max="10"
                                aria-label={"Calificación de " + alumno.nombre}
                                value={
                                  calificaciones[tareaEntregada.id_entrega] ??
                                  tareaEntregada.calificacion ??
                                  ""
                                }
                                onChange={(event) =>
                                  setCalificaciones({
                                    ...calificaciones,
                                    [tareaEntregada.id_entrega]: event.target.value,
                                  })
                                }
                              />
                            )}
                          </td>
                          <td>
                            {tareaEntregada && (
                              <input
                                className="homework-comment-input"
                                type="text"
                                aria-label={"Comentarios para " + alumno.nombre}
                                placeholder="Añadir comentario"
                                value={
                                  comentarios[tareaEntregada.id_entrega] ??
                                  tareaEntregada.comentarios ??
                                  ""
                                }
                                onChange={(event) =>
                                  setComentarios({
                                    ...comentarios,
                                    [tareaEntregada.id_entrega]: event.target.value,
                                  })
                                }
                              />
                            )}
                          </td>
                          <td>
                            {tareaEntregada && (
                              <button
                                type="button"
                                className="homework-button"
                                disabled={estaGuardando}
                                onClick={() =>
                                  handleCalificar(tareaEntregada.id_entrega)
                                }
                              >
                                <NavigationIcon name="final" size={17} />
                                {estaGuardando ? "Guardando..." : "Guardar"}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
    </section>
  );

  if (location.pathname === "/t-grade-homework") {
    return (
      <div className="contenedor-pagina">
        <Cabecera
          contenidosuperior={
            <BarraSuperior texto_cabecera={dataClase?.nombre_clase || "Calificar tareas"} />
          }
          contenidoInferior={<BarraInferior />}
        />
        <main className="homework-module">
          <section className="homework-panel">
            <header className="homework-hero">
              <div>
                <span className="homework-eyebrow">Gestión académica</span>
                <h1>Calificar tareas</h1>
                <p>
                  Revisa las entregas del grupo y registra calificaciones y
                  comentarios.
                </p>
              </div>
              <div className="homework-summary">
                <strong>{tareas.length}</strong>
                <span>{tareas.length === 1 ? "tarea disponible" : "tareas disponibles"}</span>
              </div>
            </header>
            <div className="homework-body">
              <div className="homework-workspace">{content}</div>
            </div>
          </section>
        </main>
        <PiePagina imagenSrc={require("../Assets/piepagina.jpg")} />
        <footer>Grupo Derecho & Progreso &copy; 2024</footer>
      </div>
    );
  }

  return content;
};

export default TeacherGradeHomework;
